import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { UPLOAD_CONTENT, GET_USER_CONTENT, GET_CONTENT, DELETE_CONTENT, CHECK_CONTENT_STATUS, GET_LATEST_CONTENT, TOGGLE_APPLAUSE, GET_CONTENT_BY_ID, GET_CONTENT_COMMENTS } from '../graphql/queries';
import { useAppDispatch } from '@/store';
import { 
  uploadStarted, 
  uploadProgress, 
  uploadSuccess, 
  uploadFailed, 
  resetUpload,
  removeContent
} from '../contentSlice';
import { toast } from 'react-toastify';
import { Content, UploadContentResponse, ContentGraphQLErrorClass, ContentProcessingStatus, ContentType, LatestContentResponse, UserContentResponse, ContentCommentsResponse } from '../types';
import { useState, useCallback } from 'react';
import { APP_BACKEND_URL } from '@/utils/config';
import { useTranslation } from '@/hooks/useTranslation';
import { NAMESPACES } from '@/i18n/namespaces';

// Add content namespace to the NAMESPACES enum if it doesn't exist
// This should be properly added in i18n/namespaces.ts
declare module '@/i18n/namespaces' {
  export interface NAMESPACES {
    CONTENT: 'content';
  }
}

// Helper to convert API response to our Content type
const mapResponseToContent = (response: UploadContentResponse): Content => {
  const contentData = response.content;
  return {
    id: contentData.id,
    fileName: contentData.file_name,
    mimeType: contentData.mime_type,
    size: contentData.size,
    processingStatus: contentData.processing_status as ContentProcessingStatus,
    downloadUrl: contentData.download_url,
    createdAt: contentData.created_at,
    updatedAt: contentData.updated_at,
    type: getContentTypeFromMimeType(contentData.mime_type),
    userId: contentData.user.id,
    metadata: {
      size: contentData.size,
      mimeType: contentData.mime_type
    }
  };
};

// Helper to determine content type from mime type
const getContentTypeFromMimeType = (mimeType: string): ContentType => {
  if (mimeType.startsWith('image/')) {
    return ContentType.IMAGE;
  } else if (mimeType.startsWith('video/')) {
    return ContentType.VIDEO;
  } else if (mimeType.startsWith('audio/')) {
    return ContentType.AUDIO;
  } else {
    return ContentType.DOCUMENT;
  }
};

// Hook for uploading content
export const useUploadContent = () => {
  const dispatch = useAppDispatch();
  
  const [uploadMutation] = useMutation(UPLOAD_CONTENT, {
    onCompleted: (data) => {
      try {
        const response: UploadContentResponse = data.uploadContent;
        if (response.success) {
          const content = mapResponseToContent(response);
          dispatch(uploadSuccess(content));
          toast.success(response.message || 'Content uploaded successfully!');
        } else {
          dispatch(uploadFailed(response.message || 'Upload failed'));
          toast.error(response.message || 'Upload failed');
        }
      } catch (error) {
        dispatch(uploadFailed('Error processing upload response'));
        toast.error('Failed to process the upload response');
      }
    },
    onError: (error) => {
      // Check for specific error types
      const graphQLErrors = error.graphQLErrors || [];
      
      for (const err of graphQLErrors) {
        const extensions = err.extensions || {};
        const className = extensions.class_name as string;
        
        // Handle specific error types
        switch (className) {
          case ContentGraphQLErrorClass.FILE_SIZE_EXCEEDED:
            dispatch(uploadFailed('File size exceeded'));
            toast.error(err.message || 'File size exceeded');
            return;
          
          case ContentGraphQLErrorClass.INVALID_FILE_TYPE:
            dispatch(uploadFailed('Invalid file type'));
            toast.error(err.message || 'Invalid file type');
            return;
            
          case ContentGraphQLErrorClass.UPLOAD_FAILED:
            dispatch(uploadFailed('Upload failed'));
            toast.error(err.message || 'Upload failed');
            return;
        }
      }
      
      // Default error handling
      dispatch(uploadFailed(error.message));
      toast.error('Failed to upload content');
    }
  });
  
  const uploadContent = async (file: File, onProgress?: (progress: number) => void) => {
    try {
      dispatch(uploadStarted());
      
      // Simulate progress (since Apollo doesn't support upload progress)
      if (onProgress) {
        const progressInterval = setInterval(() => {
          const currentProgress = Math.min(90, dispatch(uploadProgress(Math.min(90, Math.random() * 20))).payload);
          onProgress(currentProgress);
        }, 300);
        
        // Clear interval after upload completes or fails
        setTimeout(() => clearInterval(progressInterval), 5000);
      }
      
      const result = await uploadMutation({
        variables: { file },
      });
      
      return result.data?.uploadContent ? true : false;
    } catch (error) {
      // Error already handled in onError callback
      return false;
    }
  };
  
  const resetUploadState = () => {
    dispatch(resetUpload());
  };
  
  return { uploadContent, resetUploadState };
};

// Hook for fetching user content
export const useUserContent = (userId: string, page: number = 1, perPage: number = 100) => {
  return useQuery<UserContentResponse>(GET_USER_CONTENT, {
    variables: { user_id: userId, page, perPage },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !userId, // Skip query if no userId provided
    onError: (error) => {
      console.error('Error fetching user content:', error);
      toast.error('Failed to load user content');
    }
  });
};

// Hook for getting a single content item
export const useContent = (id: string) => {
  const { loading, error, data, refetch } = useQuery(GET_CONTENT, {
    variables: { id },
    skip: !id,
    onError: () => {
      toast.error('Failed to fetch content');
    }
  });
  
  const content = data?.getContent ? {
    id: data.getContent.id,
    fileName: data.getContent.file_name,
    mimeType: data.getContent.mime_type,
    size: data.getContent.size,
    processingStatus: data.getContent.processing_status,
    downloadUrl: data.getContent.download_url,
    createdAt: data.getContent.created_at,
    updatedAt: data.getContent.updated_at,
    type: getContentTypeFromMimeType(data.getContent.mime_type),
    userId: data.getContent.user_id,
    metadata: data.getContent.metadata ? {
      width: data.getContent.metadata.width,
      height: data.getContent.metadata.height,
      duration: data.getContent.metadata.duration,
      size: data.getContent.metadata.size,
      mimeType: data.getContent.metadata.mime_type
    } : undefined
  } : null;
  
  return { loading, error, content, refetch };
};

// Hook for deleting content
export const useDeleteContent = () => {
  const dispatch = useAppDispatch();
  
  const [deleteContentMutation, { loading }] = useMutation(DELETE_CONTENT, {
    onCompleted: (data) => {
      if (data.deleteContent?.success) {
        toast.success('Content deleted successfully');
      }
    },
    onError: () => {
      toast.error('Failed to delete content');
    }
  });
  
  const deleteContent = async (id: string) => {
    try {
      const result = await deleteContentMutation({
        variables: { id }
      });
      
      if (result.data?.deleteContent?.success) {
        dispatch(removeContent(id));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  
  return { deleteContent, loading };
};

// Hook for checking content processing status
export const useContentStatus = (id: string) => {
  const [checkStatus, { loading, data }] = useLazyQuery(CHECK_CONTENT_STATUS, {
    variables: { id },
    fetchPolicy: 'network-only'
  });
  
  const checkContentStatus = async () => {
    try {
      const result = await checkStatus();
      return result.data?.checkContentStatus?.processing_status || null;
    } catch (error) {
      return null;
    }
  };
  
  return { 
    checkContentStatus, 
    loading, 
    processingStatus: data?.checkContentStatus?.processing_status,
    downloadUrl: data?.checkContentStatus?.download_url
  };
};

// Custom upload content hook using FormData (similar to profile photo upload)
export const useUploadContentCustom = () => {
  const { t: tErrors } = useTranslation(NAMESPACES.ERRORS);
  const [loading, setLoading] = useState(false);
  
  const uploadContent = useCallback(async (file: File, onProgress?: (progress: number) => void): Promise<{ data?: { uploadContent: UploadContentResponse } }> => {
    setLoading(true);
    
    const graphqlUrl = `${APP_BACKEND_URL}/graphql`;
    
    try {
      console.log('🚀 Starting content upload...', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Simulate progress for UX
      if (onProgress) {
        const progressInterval = setInterval(() => {
          onProgress(Math.min(90, Math.random() * 80));
        }, 300);
        
        setTimeout(() => clearInterval(progressInterval), 5000);
      }

      // Create FormData for multipart upload per GraphQL multipart request spec
      const formData = new FormData();
      
      // GraphQL operation
      const operations = JSON.stringify({
        query: `
          mutation uploadContent($file: Upload!) {
            uploadContent(file: $file) {
              success
              message
              content {
                id
                file_name
                size
                mime_type
                processing_status
                download_url
                media_conversions {
                  original
                  small
                  medium
                }
                applause_count
                comment_count
                is_applauded
                is_reported
                user {
                  id
                  name
                  nickname
                  profile_photo_urls {
                    thumbnail
                    small
                    medium
                  }
                }
                created_at
                updated_at
              }
            }
          }
        `,
        variables: { file: null }
      });
      
      // Map which variables correspond to which files
      const map = JSON.stringify({
        "0": ["variables.file"]
      });
      
      // Add to FormData according to GraphQL multipart request specification
      formData.append("operations", operations);
      formData.append("map", map);
      formData.append("0", file);

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 second timeout
      
      try {
        // Send request with proper headers for multipart upload
        const response = await fetch(graphqlUrl, {
          method: 'POST',
          credentials: 'include', // Include cookies for authentication
          headers: {
            'X-Requested-With': 'XMLHttpRequest', // Critical header for AJAX requests
          },
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ HTTP Error:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }
        
        const result = await response.json();
        
        // Check for GraphQL errors
        if (result.errors && result.errors.length > 0) {
          const error = result.errors[0];
          console.error('❌ GraphQL Error:', error);
          const extensions = error.extensions || {};
          const className = extensions.class_name as string;
          
          // Handle specific error types
          switch (className) {
            case 'App\\Exceptions\\UnauthorizedException':
              toast.error('You must be logged in to upload content');
              throw new Error('Unauthorized');
            case 'App\\Exceptions\\ValidationException':
              toast.error(error.message || 'Please select a valid file');
              throw new Error('Validation failed');
            case 'App\\Exceptions\\FileTooLargeException':
              toast.error('File size is too large. Please select a smaller file');
              throw new Error('File too large');
            case ContentGraphQLErrorClass.FILE_SIZE_EXCEEDED:
              toast.error('File size exceeded');
              throw new Error('File size exceeded');
            case ContentGraphQLErrorClass.INVALID_FILE_TYPE:
              toast.error('Invalid file type');
              throw new Error('Invalid file type');
            case ContentGraphQLErrorClass.UPLOAD_FAILED:
              toast.error('Upload failed');
              throw new Error('Upload failed');
            default:
              toast.error(error.message || tErrors('graphql.default'));
              throw new Error(error.message || 'Upload failed');
          }
        }
        
        console.log('✅ Content upload successful!');
        
        // Complete progress
        if (onProgress) {
          onProgress(100);
        }
        
        setLoading(false);
        return { data: result.data };
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          throw new Error('Upload timed out after 30 seconds');
        }
        throw fetchError;
      }
      
    } catch (error) {
      console.error('💥 Content upload error:', error);
      setLoading(false);
      
      // If we haven't already shown a specific error message, show a generic one
      if (error instanceof Error) {
        if (!error.message.includes('Unauthorized') && 
            !error.message.includes('Validation') && 
            !error.message.includes('File too large') &&
            !error.message.includes('File size exceeded') &&
            !error.message.includes('Invalid file type') &&
            !error.message.includes('Upload failed')) {
          toast.error(`Upload failed: ${error.message}`);
        }
      } else {
        toast.error(tErrors('graphql.default'));
      }
      
      throw error;
    }
  }, [tErrors]);

  return [uploadContent, { loading }] as const;
};

// Hook for fetching latest content
export const useLatestContent = (page: number = 1, perPage: number = 100) => {
  return useQuery<LatestContentResponse>(GET_LATEST_CONTENT, {
    variables: { page, perPage },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (error) => {
      console.error('Error fetching latest content:', error);
      toast.error('Failed to load content');
    }
  });
};

// Hook for toggling applause on content
export const useToggleApplause = () => {
  const [toggleApplauseMutation, { loading }] = useMutation(TOGGLE_APPLAUSE, {
    // Update the cache optimistically
    update: (cache, { data }) => {
      if (data?.toggleApplause) {
        // Update all queries that might contain this content
        cache.modify({
          fields: {
            latestContent(existingContent = { data: [] }) {
              const updatedData = existingContent.data.map((item: any) => {
                if (item.id === data.toggleApplause.id) {
                  return {
                    ...item,
                    applause_count: data.toggleApplause.applause_count,
                    is_applauded: data.toggleApplause.is_applauded,
                    user: {
                      ...item.user,
                      applauses_count: data.toggleApplause.user.applauses_count
                    }
                  };
                }
                return item;
              });
              
              return {
                ...existingContent,
                data: updatedData
              };
            },
            userContent(existingContent = { data: [] }) {
              const updatedData = existingContent.data.map((item: any) => {
                if (item.id === data.toggleApplause.id) {
                  return {
                    ...item,
                    applause_count: data.toggleApplause.applause_count,
                    is_applauded: data.toggleApplause.is_applauded,
                    user: {
                      ...item.user,
                      applauses_count: data.toggleApplause.user.applauses_count
                    }
                  };
                }
                return item;
              });
              
              return {
                ...existingContent,
                data: updatedData
              };
            }
          }
        });
      }
    },
    onError: (error) => {
      console.error('Toggle applause error:', error);
      toast.error('Failed to toggle applause. Please try again.');
    }
  });

  return [toggleApplauseMutation, { loading }] as const;
};

// Hook for fetching content by ID with fresh data
export const useContentById = (id: string, skip: boolean = false) => {
  return useQuery(GET_CONTENT_BY_ID, {
    variables: { id },
    skip,
    fetchPolicy: 'cache-and-network', // Always try to get fresh data
    errorPolicy: 'all',
    onError: (error) => {
      console.error('Error fetching content by ID:', error);
      toast.error('Failed to load content details');
    }
  });
};

// Hook for fetching content comments
export const useContentComments = (
  contentId: string, 
  page: number = 1, 
  perPage: number = 15, 
  topLevelOnly: boolean = true,
  skip: boolean = false
) => {
  return useQuery<ContentCommentsResponse>(GET_CONTENT_COMMENTS, {
    variables: { 
      content_id: contentId, 
      page, 
      perPage, 
      top_level_only: topLevelOnly 
    },
    skip: skip || !contentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onError: (error) => {
      console.error('Error fetching content comments:', error);
      toast.error('Failed to load comments');
    }
  });
};

 